import { Add, ContentCopy, Delete } from '@mui/icons-material';
import { Box, Button, IconButton, Paper, Stack, Switch, ToggleButton, Typography } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { SchoolShiftFormInput } from '../../../services/SchoolShift/schemas';
import { ITimeSlot } from 'services/SchoolShift/types';

interface Props {
  index: number;
}

export default function SchoolShiftDayRow({ index }: Props) {
  const { control, getValues, setValue, trigger, clearErrors } = useFormContext<SchoolShiftFormInput>();
  const { t } = useTranslation();
  const { fields: slots, append, remove } = useFieldArray({ control, name: `weeklySchedule.${index}.timeSlots` });

  const isActive = useWatch({ control, name: `weeklySchedule.${index}.active` });
  const dayName = useWatch({ control, name: `weeklySchedule.${index}.dayName` });

  const { errors } = useFormState({ 
    control, 
    name: `weeklySchedule.${index}.timeSlots`
  });

  const cachedSlots = useRef<ITimeSlot[]>([]);

  const handleToggleDay = (checked: boolean) => {
    if (checked) {
      const hasCache = cachedSlots.current.length > 0;
      
      const slotsToRestore = hasCache 
        ? cachedSlots.current 
        : [{ start: '', end: '', isEnter: false, isExit: false }];
        
      setValue(`weeklySchedule.${index}.active`, true);
      setValue(`weeklySchedule.${index}.timeSlots`, slotsToRestore);
      
      clearErrors('weeklySchedule.root' as any); 
      clearErrors('weeklySchedule.message' as any); 
      
      const hasAnyValueTyped = slotsToRestore.some(slot => slot.start || slot.end);

      if (hasAnyValueTyped) {
        trigger(`weeklySchedule.${index}.timeSlots`);
      } else {
        clearErrors(`weeklySchedule.${index}.timeSlots`);
      }
      
    } else {
      cachedSlots.current = getValues(`weeklySchedule.${index}.timeSlots`);
      setValue(`weeklySchedule.${index}.active`, false);
      setValue(`weeklySchedule.${index}.timeSlots`, []);
      clearErrors(`weeklySchedule.${index}.timeSlots`);
    }
  };

  const handleCopyMonday = () => {
    const mondaySlots = getValues('weeklySchedule.0.timeSlots');
    for (let i = 1; i <= 4; i++) {
      setValue(`weeklySchedule.${i}.active`, true);
      setValue(`weeklySchedule.${i}.timeSlots`, structuredClone(mondaySlots));
    }

    trigger('weeklySchedule');
  };

  const handleSmartAddSlot = () => {
    const currentSlots = getValues(`weeklySchedule.${index}.timeSlots`);
    const lastSlot = currentSlots[currentSlots.length - 1];
    const newStart = lastSlot?.end || '';
    append({ start: newStart, end: '', isEnter: false, isExit: false });
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, md: 3 },
        transition: '0.3s',
        opacity: isActive ? 1 : 0.5,
        borderColor: isActive ? 'primary.main' : 'divider',
        bgcolor: isActive ? 'rgba(37, 99, 235, 0.02)' : 'transparent',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: isActive ? 2 : 0 }}>
        <Typography variant="subtitle1" fontWeight="bold">{dayName}</Typography>
        <Controller
          name={`weeklySchedule.${index}.active`}
          control={control}
          render={({ field }) => (
            <Switch checked={field.value} onChange={(e) => handleToggleDay(e.target.checked)} />
          )}
        />
      </Stack>

      {isActive && (
        <Stack spacing={2}>
          {slots.map((slot, sIdx) => {
            const slotErrors = errors?.weeklySchedule?.[index]?.timeSlots?.[sIdx];

            return (
              <Stack key={slot.id} spacing={0.5}>
                
                <Stack
                  direction="row"
                  spacing={{ xs: 2, md: 3 }}
                  alignItems="center"
                  sx={{ bgcolor: 'background.default', p: 1.5, borderRadius: 2 }}
                >
                  <Box sx={{ 
                    display: { xs: 'grid', md: 'flex' }, 
                    gridTemplateColumns: { xs: '1fr auto 1fr', md: 'none' }, 
                    rowGap: { xs: 1.5, md: 0 }, 
                    columnGap: { xs: 1, md: 3 }, 
                    alignItems: 'center',
                    flex: { xs: 1, md: 'unset' }
                  }}>
                    
                    <Controller
                      name={`weeklySchedule.${index}.timeSlots.${sIdx}.start`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TimePicker
                          label={t('schoolShiftPage.startLabel')}
                          ampm={false}
                          timeSteps={{ minutes: 1 }} 
                          value={field.value ? dayjs(field.value, 'HH:mm') : null} 
                          onChange={(newValue) => {
                            const timeString = newValue && newValue.isValid() ? newValue.format('HH:mm') : '';
                            field.onChange(timeString);
                            trigger(`weeklySchedule.${index}.timeSlots.${sIdx}`);
                          }}
                          slotProps={{
                            textField: {
                              size: "small",
                              error: !!fieldState.error,
                              sx: { width: { xs: '100%', md: 150 } }
                            },
                            actionBar: { actions: [] } 
                          }}
                        />
                      )}
                    />

                    <Typography color="text.secondary" sx={{ alignSelf: 'center', textAlign: 'center' }}>
                      -
                    </Typography>

                    <Controller
                      name={`weeklySchedule.${index}.timeSlots.${sIdx}.end`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <TimePicker
                          label={t('schoolShiftPage.endLabel')}
                          ampm={false}
                          timeSteps={{ minutes: 1 }} 
                          value={field.value ? dayjs(field.value, 'HH:mm') : null}
                          onChange={(newValue) => {
                            const timeString = newValue && newValue.isValid() ? newValue.format('HH:mm') : '';
                            field.onChange(timeString);
                            trigger(`weeklySchedule.${index}.timeSlots.${sIdx}`);
                          }}
                          slotProps={{
                            textField: {
                              size: "small",
                              error: !!fieldState.error,
                              sx: { width: { xs: '100%', md: 150 } }
                            },
                            actionBar: { actions: [] } 
                          }}
                        />
                      )}
                    />

                    <Box sx={{ display: { xs: 'contents', md: 'flex' }, gap: { md: 1.5 } }}>
                      <Controller
                        name={`weeklySchedule.${index}.timeSlots.${sIdx}.isEnter`}
                        control={control}
                        render={({ field }) => (
                          <ToggleButton
                            size="small"
                            value="in"
                            selected={field.value}
                            onClick={() => field.onChange(!field.value)}
                            sx={{ width: { xs: '100%', md: 'auto' }, px: { md: 2 } }}
                          >
                            {t('schoolShiftPage.enter')}
                          </ToggleButton>
                        )}
                      />

                      <Box sx={{ display: { xs: 'block', md: 'none' } }} />

                      <Controller
                        name={`weeklySchedule.${index}.timeSlots.${sIdx}.isExit`}
                        control={control}
                        render={({ field }) => (
                          <ToggleButton
                            size="small"
                            value="out"
                            selected={field.value}
                            onClick={() => field.onChange(!field.value)}
                            sx={{ width: { xs: '100%', md: 'auto' }, px: { md: 2 } }}
                          >
                            {t('schoolShiftPage.exit')}
                          </ToggleButton>
                        )}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
                  
                  <IconButton
                    color="error"
                    size="small"
                    disabled={slots.length === 1}
                    onClick={() => remove(sIdx)}
                    sx={{ flexShrink: 0 }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>

                </Stack>

                {(slotErrors?.start || slotErrors?.end) && (
                  <Typography variant="caption" color="error" sx={{ px: 1.5 }}>
                    {slotErrors?.end?.message || slotErrors?.start?.message}
                  </Typography>
                )}
              </Stack>
            );
          })}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            {slots.length < 4 && (
              <Button
                startIcon={<Add />}
                size="small"
                onClick={handleSmartAddSlot}
                sx={{ flexShrink: 0 }}
              >
                {t('schoolShiftPage.addTimeSlot')}
              </Button>
            )}
            {index === 0 && (
              <Button startIcon={<ContentCopy />} color="inherit" size="small" onClick={handleCopyMonday}>
                {t('schoolShiftPage.copyToWeekdays')}
              </Button>
            )}
          </Stack>
        </Stack>
      )}
    </Paper>
  );
}